import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useOutlet, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Link,
  IconButton,
  Popover,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Stack
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PublicPortalAppearanceProvider, usePublicPortalAppearance } from '../../context/PublicPortalAppearanceContext';
import { resolveBrandingLogoImageUrl } from '../../utils/publicAssetUrl';
import { getPublicApiBase } from '../../utils/publicApi';
import { PublicBrandingIdentity } from './PublicBrandingIdentity';
import PublicSiteAccessButtons from './PublicSiteAccessButtons';
import PortalTemplatePickerGrid from './PortalTemplatePickerGrid';

function PublicLayoutChrome({ portalBasePath = '/query', children }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const embedMode = searchParams.get('embed') === '1';
  const outlet = useOutlet();
  const portalHome = portalBasePath || '/query';
  const marketingHome = '/';
  const section = (id) => `${portalHome}#${id}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const {
    layoutMode,
    setLayoutMode,
    templateId,
    setTemplateId,
    contentTemplateId,
    setContentTemplateId,
    splitBrowseTemplateId,
    splitPreviewTemplateId,
    setSplitBrowseTemplateId,
    setSplitPreviewTemplateId,
    clearSplitBrowseTemplateOverride,
    clearSplitPreviewTemplateOverride,
    splitBrowseHasOverride,
    splitPreviewHasOverride,
    pageBackgroundCss,
    publicBranding
  } = usePublicPortalAppearance();

  const logoSrc = publicBranding.portalLogoPath
    ? resolveBrandingLogoImageUrl(publicBranding.portalLogoPath)
    : null;

  const [lookAnchor, setLookAnchor] = useState(null);
  const lookOpen = Boolean(lookAnchor);

  const [trainMenuAnchor, setTrainMenuAnchor] = useState(null);
  const [trainingCategories, setTrainingCategories] = useState([]);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [benchesPublic, setBenchesPublic] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const base = getPublicApiBase().replace(/\/+$/, '');
    axios
      .get(`${base}/training/catalog`)
      .then((r) => {
        const d = r.data?.data;
        if (cancelled || !d?.enabled || !Array.isArray(d.categories)) return;
        setTrainingCategories(d.categories);
      })
      .catch(() => {
        if (!cancelled) setTrainingCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const base = getPublicApiBase().replace(/\/+$/, '');
    axios
      .get(`${base}/visibility`)
      .then((r) => {
        const v = r.data?.data || {};
        if (!cancelled) setBenchesPublic(v.benches === 'public');
      })
      .catch(() => {
        if (!cancelled) setBenchesPublic(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const base = getPublicApiBase().replace(/\/+$/, '');
    axios
      .get(`${base}/signup-config`)
      .then((r) => {
        if (!cancelled) setSignupEnabled(r.data?.data?.enabled !== false);
      })
      .catch(() => {
        if (!cancelled) setSignupEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const closeTrainMenu = () => setTrainMenuAnchor(null);

  const barIsLight = ['light', 'aqua', 'orchid'].includes(templateId);

  if (embedMode) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: pageBackgroundCss,
          backgroundAttachment: 'fixed'
        }}
      >
        {children ?? outlet}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: barIsLight ? alpha('#ffffff', 0.9) : alpha('#0f172a', 0.88),
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          color: barIsLight ? 'text.primary' : 'grey.100',
          borderBottom: '1px solid',
          borderColor: barIsLight ? alpha('#0f172a', 0.08) : alpha('#fff', 0.1),
          boxShadow: barIsLight ? '0 8px 32px rgba(15,23,42,0.08)' : '0 8px 32px rgba(0,0,0,0.35)'
        }}
      >
        <Toolbar
          sx={{
            gap: 0.5,
            flexWrap: 'wrap',
            py: 1.25,
            alignItems: 'center',
            '& .MuiIconButton-root': { color: 'inherit' }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexGrow: 1,
              minWidth: 0,
              cursor: 'pointer'
            }}
            onClick={() => navigate(marketingHome)}
          >
            <PublicBrandingIdentity
              variant="toolbar"
              toolbarColorScheme={barIsLight ? 'light' : 'dark'}
              logoSrc={logoSrc}
              companyName={publicBranding.companyName}
              companyAddress={publicBranding.companyAddress}
              companyCity={publicBranding.companyCity}
              companyZip={publicBranding.companyZip}
              companyWebsite={publicBranding.companyWebsite}
              companyContactEmail={publicBranding.companyContactEmail}
              companyTagline={publicBranding.companyTagline}
              companyNameStyle={publicBranding.companyNameStyle}
              companyTaglineStyle={publicBranding.companyTaglineStyle}
            />
          </Box>
          <Button
            component={RouterLink}
            to={marketingHome}
            startIcon={<ArrowBackIcon />}
            color="inherit"
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              mr: 0.5,
              ...(barIsLight
                ? {}
                : {
                    borderColor: alpha('#fff', 0.35),
                    '&:hover': { bgcolor: alpha('#fff', 0.06) }
                  })
            }}
          >
            Back to Home
          </Button>
          <PublicSiteAccessButtons active="query" barIsLight={barIsLight} size="small" />
          <Link
            component={RouterLink}
            to={section('portal-jobs')}
            underline="hover"
            color="inherit"
            sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: '0.875rem', px: 0.5 }}
          >
            Jobs
          </Link>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Button
              color="inherit"
              size="small"
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setTrainMenuAnchor(e.currentTarget)}
              sx={{ fontSize: '0.875rem', textTransform: 'none', px: 0.5, minWidth: 0 }}
            >
              Trainings
            </Button>
            <Menu
              anchorEl={trainMenuAnchor}
              open={Boolean(trainMenuAnchor)}
              onClose={closeTrainMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { minWidth: 220 } } }}
            >
              <MenuItem
                component={RouterLink}
                to={section('portal-training')}
                onClick={closeTrainMenu}
              >
                All trainings
              </MenuItem>
              {trainingCategories.map((cat) => (
                <MenuItem
                  key={cat.id}
                  component={RouterLink}
                  to={`${portalHome}#portal-training-cat-${cat.id}`}
                  onClick={closeTrainMenu}
                >
                  {cat.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Link
            component={RouterLink}
            to={section('portal-exams')}
            underline="hover"
            color="inherit"
            sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: '0.875rem', px: 0.5 }}
          >
            Exams
          </Link>
          {benchesPublic ? (
            <Link
              component={RouterLink}
              to={section('portal-benches')}
              underline="hover"
              color="inherit"
              sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: '0.875rem', px: 0.5 }}
            >
              Benches
            </Link>
          ) : null}

          <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, sm: 0.5 } }}>
            <Tooltip title="Split frame — list and preview">
              <IconButton
                size="medium"
                color={layoutMode === 'split' ? 'primary' : 'inherit'}
                onClick={() => setLayoutMode('split')}
                aria-label="Split frame layout"
              >
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Straight — single scrolling page">
              <IconButton
                size="medium"
                color={layoutMode === 'regular' ? 'primary' : 'inherit'}
                onClick={() => setLayoutMode('regular')}
                aria-label="Straight scrolling layout"
              >
                <ViewStreamIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Colors & templates — saved in this browser">
              <IconButton size="medium" onClick={(e) => setLookAnchor(e.currentTarget)} aria-label="Page look and templates">
                <TuneIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Popover
            open={lookOpen}
            anchorEl={lookAnchor}
            onClose={() => setLookAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  p: 2.5,
                  width: 'min(420px, calc(100vw - 24px))',
                  maxHeight: 'min(90vh, 900px)',
                  overflow: 'auto',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: theme.shadows[8]
                }
              }
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
              Appearance
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Pick a page atmosphere, then tune cards and split columns. Choices stay on this device.
            </Typography>

            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.08, color: 'text.secondary' }}>
              Page atmosphere
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              Full-page gradient behind everything.
            </Typography>
            <PortalTemplatePickerGrid selectedId={templateId} onSelect={setTemplateId} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.08, color: 'text.secondary' }}>
              Single-page cards
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              Scroll layout: job, training, and exam cards. Split panes use this too until you set a left/right override
              below.
            </Typography>
            <PortalTemplatePickerGrid selectedId={contentTemplateId} onSelect={setContentTemplateId} />
            <Stack direction="row" flexWrap="wrap" justifyContent="flex-end" gap={0.5} sx={{ mt: 1.25 }}>
              <Button size="small" variant="text" onClick={() => setContentTemplateId(templateId)}>
                Match page atmosphere
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  clearSplitBrowseTemplateOverride();
                  clearSplitPreviewTemplateOverride();
                }}
              >
                Both split panes follow cards
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.08, color: 'text.secondary' }}>
              Split — left (browse)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              List column: jobs / trainings / exams tabs and selectors.
            </Typography>
            <PortalTemplatePickerGrid selectedId={splitBrowseTemplateId} onSelect={setSplitBrowseTemplateId} />
            {splitBrowseHasOverride ? (
              <Button size="small" variant="text" sx={{ mt: 1, display: 'block' }} onClick={clearSplitBrowseTemplateOverride}>
                Follow card palette
              </Button>
            ) : null}

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.08, color: 'text.secondary' }}>
              Split — right (preview)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              Detail pane: filters, descriptions, and enrollment actions.
            </Typography>
            <PortalTemplatePickerGrid selectedId={splitPreviewTemplateId} onSelect={setSplitPreviewTemplateId} />
            {splitPreviewHasOverride ? (
              <Button
                size="small"
                variant="text"
                sx={{ mt: 1, display: 'block' }}
                onClick={clearSplitPreviewTemplateOverride}
              >
                Follow card palette
              </Button>
            ) : null}
          </Popover>

          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            startIcon={<LoginIcon />}
            size="small"
            sx={
              barIsLight
                ? {}
                : {
                    color: 'common.white',
                    borderColor: alpha('#fff', 0.45),
                    '&:hover': { borderColor: 'common.white', bgcolor: alpha('#fff', 0.06) }
                  }
            }
          >
            Log in
          </Button>
          {signupEnabled && (
            <Button component={RouterLink} to="/signup" variant="contained" startIcon={<PersonAddIcon />} size="small">
              Sign up
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          background: pageBackgroundCss,
          backgroundAttachment: 'fixed'
        }}
      >
        {children ?? outlet}
      </Box>
    </Box>
  );
}

/**
 * Anonymous query portal chrome (jobs / training / exams). Marketing landing is at `/`.
 */
const PublicLayout = ({ portalBasePath = '/query', children }) => (
  <PublicPortalAppearanceProvider>
    <PublicLayoutChrome portalBasePath={portalBasePath}>{children}</PublicLayoutChrome>
  </PublicPortalAppearanceProvider>
);

export default PublicLayout;
